import axios from 'axios';
import { getStorageItem, setStorageItem } from './storageManager';

declare const window: any;

/**
 * Gets zip code from various sources in priority order:
 * 1. window.app_data.zipCode
 * 2. localStorage locationCoords
 * 3. Fresh location request
 * @returns the zip code or an empty string if not available
 */
const getZipCode = async (): Promise<string> => {
  // Check window.app_data first
  let zipCode = window?.app_data?.zipCode || '';
  if (zipCode) return zipCode;

  try {
    // Check localStorage
    const locationCoords = getStorageItem('locationCoords') || {};
    if (locationCoords?.postalCode) {
      zipCode = locationCoords.postalCode;
    } else {
      // Request fresh location
      const { requestLocation } = await import('../components/Viewplans/helper');
      const locationObj = await requestLocation();
      zipCode = locationObj?.postalCode || '';
    }

    // Cache in window.app_data if available
    if (zipCode && window.app_data) {
      window.app_data.zipCode = zipCode;
    }

    return zipCode;
  } catch (error) {
    console.error('Error getting zip code:', error);
    return '';
  }
};

/**
 * Fetches teams data for a given zip code
 * @param zipCode the zip code to fetch teams for
 * @returns an array of teams or an empty array if there's an error or no teams are available
 */
const fetchTeamsData = async (zipCode: string): Promise<any[]> => {
  if (!zipCode) {
    return [];
  }

  try {
    const site = window?.app_data?.site?.siteInternalName || '';
    const apiBaseUrl = window?.app_data?.appcmsMain?.apiConfig?.apiBaseUrl;
    const apiBaseCachedUrl = window?.app_data?.appcmsMain?.apiConfig?.apiBaseCachedUrl;

    const response = await axios.get(`${apiBaseCachedUrl||apiBaseUrl}/v4/content/personalization/choices`, {
      params: {
        site,
        contentType: 'team',
        zipcode: zipCode
      },
      headers: {
        'x-api-key': window?.xApiKey || ''
      }
    });

    return response?.data?.teams || [];
  } catch (error) {
    console.error('Error fetching teams data:', error);
    return [];
  }
};

/**
 * Retrieves user preferred team IDs from sessionStorage
 * @returns {Array<string>} Array of team IDs or empty array
 */
const getUserPreferredTeamIds = (): string[] => {
  try {
    const authStore = getStorageItem('AuthenticationStore');
    const userTeams = authStore?.user?.profiles?.[0]?.contentPersonalization?.teams;

    if (Array.isArray(userTeams) && userTeams.length > 0) {
      return userTeams.map(team => team?.id).filter(id => id != null);
    }

    return [];
  } catch (error) {
    console.error('Error parsing user preferred teams from sessionStorage');
    return [];
  }
};

/**
 * Retrieves zipcode-location based team IDs from sessionStorage
 * @returns {Array} Array of team IDs or empty array
 */
const getLocationBasedTeamIds = () => {
  try {
    const teamsData = getStorageItem('teamsData') || [];
    
    if (Array.isArray(teamsData) && teamsData.length > 0) {
      const teamIds = teamsData.map(team => team?.id).filter(id => id != null);
      return teamIds;
    }
    
    return [];
  } catch (error) {
    console.error('Error parsing teamsData from sessionStorage');
    return [];
  }
};

/**
 * Retrieves team IDs with priority: user preferred > location-based > fallback team IDs
 * @param fallbackTeamIds team IDs to use when personalization data is unavailable (e.g. CMS module defaults)
 * @returns {Array<string>} Array of team IDs or fallback team IDs
 */
const getPersonalizedTeamIds = (fallbackTeamIds: string[] = []): string[] => {
  const userPreferredTeamIds = getUserPreferredTeamIds();
  if (userPreferredTeamIds?.length > 0) return userPreferredTeamIds;

  const locationBasedTeamIds = getLocationBasedTeamIds();
  if (locationBasedTeamIds?.length > 0) return locationBasedTeamIds;

  return Array.isArray(fallbackTeamIds)
    ? fallbackTeamIds.filter(id => id != null)
    : [];
};


/**
 * Checks teams availability based on user's zipcode and caches the result
 * @returns true if teams are available, false otherwise
 */
const checkTeamsAvailability = async (): Promise<boolean> => {
  try {
    const userPreferredTeam = getUserPreferredTeamIds()
    if (Array.isArray(userPreferredTeam) && userPreferredTeam.length > 0) return true
    
    const zipCode = await getZipCode();
    const teams = await fetchTeamsData(zipCode);
    
    // Cache teams data
    setStorageItem('teamsData', teams);
    
    return teams.length > 0;
  } catch (error) {
    console.error('Error checking teams availability:', error);
    setStorageItem('teamsData', []);
    return false;
  }
};

export { 
  getZipCode, fetchTeamsData,
  getLocationBasedTeamIds, getUserPreferredTeamIds,
  getPersonalizedTeamIds, checkTeamsAvailability, 
};